/**
 * SRES · shared motion vocabulary
 *
 * Every marketing page shares the same animation "grammar" so the site
 * feels coherent end-to-end. Subtle & professional — no bounce, no
 * spring overshoot, no cinematic reveals. The reader should notice
 * the *content*, not the motion.
 *
 * All variants respect prefers-reduced-motion via Framer's own
 * useReducedMotion hook when consumed inside components.
 */

// Standard viewport for scroll-triggered reveals.
export const revealViewport = { once: true, amount: 0.2, margin: "0px 0px -80px 0px" };

// Master ease — Apple/Linear-style cubic-bezier, no overshoot.
export const easeOutSoft = [0.22, 0.61, 0.36, 1];

// Single element rising into place from just below.
export const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: easeOutSoft },
  },
};

// Slightly smaller drift — for tight groups (badges, KPIs).
export const fadeUpSmall = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOutSoft },
  },
};

// Just fade — for backgrounds & decorative layers.
export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.8, ease: easeOutSoft } },
};

// Parent variant that staggers children — used for grids & lists.
export const staggerParent = (stagger = 0.08, delay = 0) => ({
  hidden: {},
  show: {
    transition: { staggerChildren: stagger, delayChildren: delay },
  },
});

// Hero-scale reveal — a beat slower, slightly longer distance.
export const heroFadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: easeOutSoft },
  },
};

// SVG path draw — used for variogram / pipeline curves.
export const drawPath = (duration = 1.4, delay = 0.2) => ({
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration, ease: easeOutSoft, delay },
      opacity: { duration: 0.2, delay },
    },
  },
});
